import { CookieParseOptions, CookieSerializeOptions, parse, serialize } from 'cookie';

export { CookieParseOptions, CookieSerializeOptions };

/**
 * 解析 Cookie 字符串
 * @returns 返回一个对象, 包含 Cookie 中的所有键值对
 * @description
 * 此函数来自第三方库 [cookie](https://github.com/jshttp/cookie#cookieparsestr-options) ([source](https://github.com/jshttp/cookie/blob/6e5b36beee4ce605b03e31a95575e8d6031fdb3d/index.js#L49))
 * @example
 * ```js
 * parseCookies("a=b; c=d; e=");  // -> { a: "b", c: "d", e: "" }
 * ```
 */
export const parseCookies: (cookies: string, options?: CookieParseOptions) => Record<string, string> = parse;

/**
 * 将键值对序列化成 `Set-Cookie` 请求头的格式
 * @description
 * 此函数来自第三方库 [cookie](https://github.com/jshttp/cookie#cookieserializename-value-options) ([source](https://github.com/jshttp/cookie/blob/6e5b36beee4ce605b03e31a95575e8d6031fdb3d/index.js#L101))
 * @example
 * ```js
 * serializeCookie("foo", "bar");   // -> "foo=bar"
 * serializeCookie("foo", "bar", { path: "/" });           // -> "foo=bar; Path=/"
 * serializeCookie("foo", "bar", { maxAge: 666 });         // -> "foo=bar; Max-Age=666"
 * serializeCookie("foo", "bar", { secure: true });        // -> "foo=bar; Secure"
 * serializeCookie("foo", "bar", { httpOnly: true });      // -> "foo=bar; HttpOnly"
 * serializeCookie("foo", "bar", { domain: ".qq.com" });   // -> "foo=bar; Domain=.qq.com"
 * serializeCookie("foo", "bar", { sameSite: "strict" });  // -> "foo=bar; SameSite=Strict"
 * serializeCookie("foo", "bar", { expires: new Date(2020, 0, 1) });
 *  // -> "foo=bar; Expires=Tue, 31 Dec 2019 16:00:00 GMT"
 * ```
 */
export const serializeCookie: (key: string, value: string, options?: CookieSerializeOptions) => string = serialize;

/**
 * 获取浏览器中的所有 Cookie, 只能在浏览器中使用
 * @see [[parseCookies]]
 * @example
 * ```js
 * getCookies();  // -> { imei: "866370033011966", ... }
 * ```
 */
export function getCookies(options?: CookieParseOptions): Record<string, string> {
  return parseCookies(document.cookie, options);
}

/**
 * 获取浏览器中当前的某一个 Cookie 的值, 只能在浏览器中使用
 * @see [[getCookies]]
 * @example
 * ```js
 * getCookie("imei");   // -> "866370033011966"
 * getCookie("wrong");  // -> undefined
 * ```
 */
export function getCookie(key: string, options?: CookieParseOptions): string | undefined {
  return getCookies(options)[key];
}

/**
 * 将 Cookie 设置到浏览器中, 只能在浏览器中使用
 * @returns 返回序列化后的 Cookie
 * @see [[serializeCookie]]
 * @example
 * ```js
 * setCookie("foo", "bar");   // -> foo=bar
 * ```
 */
export function setCookie(key: string, value: string, options?: CookieSerializeOptions): string {
  const setCookieStr = serializeCookie(key, value, options);
  document.cookie = setCookieStr;

  return setCookieStr;
}
